import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchMatterRequestTypes } from './useFetchMatterRequestTypes';
import { useLanguage } from '../context/LanguageContext';
import { getTranslationFromTranslatableEntity } from '../constants/matter';
import { IActiveSortState } from '../interface/common';

export const useMatterRequestTypeSelection = (isCustomerTypeSelection: boolean) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });
  const { language: appLanguage } = useLanguage();
  const queryClient = useQueryClient();

  const {
    data: matterRequestTypeList,
    isLoading: isMatterRequestTypesLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMatterRequestTypes(
    {
      pageSize: 50,
      search: searchValue,
    },
    !isCustomerTypeSelection,
  );

  const refreshMatterRequestTypeList = () => {
    queryClient.removeQueries({ queryKey: ['matter-request-types'] });
    return refetch();
  };

  const sortedMatterRequestTypeList = useMemo(
    () =>
      matterRequestTypeList
        ? [...matterRequestTypeList].sort((a, b) => {
            const comparison = getTranslationFromTranslatableEntity(
              a.translations,
              appLanguage,
            ).localeCompare(
              getTranslationFromTranslatableEntity(b.translations, appLanguage),
              appLanguage,
            );
            return activeSort.direction === 'DESC' ? -comparison : comparison;
          })
        : matterRequestTypeList,
    [matterRequestTypeList, appLanguage, activeSort.direction],
  );

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && sortedMatterRequestTypeList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, sortedMatterRequestTypeList]);

  return {
    matterRequestTypeList: sortedMatterRequestTypeList,
    isMatterRequestTypesLoading,
    isFetching,
    searchValue,
    setSearchValue,
    paginationRef: ref,
    hasNextPage,
    activeSort,
    setActiveSort,
    refreshMatterRequestTypeList,
  };
};
